import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

/**
 * WeeklyActivityDots — de week als één samenhangend oefenspoor.
 *
 * Zeven dagknopen op één afgeronde baan. Geoefende dagen zijn gevulde knopen
 * met een vinkje; het baansegment tússen twee opeenvolgende geoefende dagen
 * licht op. Daardoor leest een echte reeks als één ononderbroken lint in plaats
 * van als losse stippen — precies het gedrag dat we willen belonen.
 *
 * Deze component stond eerder in het design system. Hij is hierheen gehaald
 * zodat het project op zichzelf compleet is: bij het publiceren naar Git zou een
 * marker naar een externe bundel een niet-bouwbaar gat achterlaten.
 *
 * De oude variant (losse sterren per dag) is hiermee vervangen. Twee
 * implementaties met dezelfde naam naast elkaar was al als opruimpunt
 * gesignaleerd in MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 (R2-3).
 */

export interface DayActivity {
  label: string;
  /** Heeft de leerling deze dag geoefend. */
  practiced: boolean;
  /**
   * Optionele intensiteit 0–1 (bijvoorbeeld genormaliseerde minuten of aantal
   * vragen). Schaalt de knoop subtiel mee. Ontbreekt de waarde, dan krijgt een
   * geoefende dag de volle grootte.
   */
  intensity?: number;
  isToday?: boolean;
}
interface WeeklyActivityDotsProps {
  /**
   * Structureel getypeerd in plaats van gebonden aan `DayActivity` uit
   * `utils/activityHelpers`: die variant heeft een verplichte `questionCount`
   * die deze component niet nodig heeft. Zo blijven beide bronnen bruikbaar
   * zonder dat de een de ander hoeft te kennen.
   */
  weeklyActivity: Array<{
    label: string;
    practiced: boolean;
    intensity?: number;
    isToday?: boolean;
  }>;
  /** Vulkleur van het spoor. */
  color?: 'primary' | 'emerald' | 'indigo' | 'slate';
  /** Aantal dagen dat het weekdoel is. Toont een samenvattingsregel. */
  weeklyGoal?: number;
  /** Verberg de samenvatting onder het spoor. */
  hideSummary?: boolean;
  className?: string;
}
const TRACK_FILL: Record<string, string> = {
  primary: 'bg-orange-500',
  emerald: 'bg-emerald-500',
  indigo: 'bg-indigo-500',
  slate: 'bg-slate-700'
};
const NODE_FILL: Record<string, string> = {
  primary: 'bg-orange-500 border-orange-600',
  emerald: 'bg-emerald-500 border-emerald-600',
  indigo: 'bg-indigo-500 border-indigo-600',
  slate: 'bg-slate-700 border-slate-800'
};
const HALO: Record<string, string> = {
  primary: 'bg-orange-400/30',
  emerald: 'bg-emerald-400/30',
  indigo: 'bg-indigo-400/30',
  slate: 'bg-slate-400/30'
};

/** 26px bij lage intensiteit, 38px bij volle inzet. */
function nodeSize(day: {
  practiced: boolean;
  intensity?: number;
}): number {
  if (!day.practiced) return 26;
  const intensity = day.intensity ?? 1;
  return Math.round(26 + Math.min(Math.max(intensity, 0), 1) * 12);
}
export function WeeklyActivityDots({
  weeklyActivity,
  color = 'emerald',
  weeklyGoal,
  hideSummary = false,
  className = ''
}: WeeklyActivityDotsProps) {
  const practicedCount = weeklyActivity.filter(d => d.practiced).length;
  const goalReached = weeklyGoal ? practicedCount >= weeklyGoal : false;

  // Eén beschrijvend label voor het geheel. Zeven losse knopen voorlezen levert
  // een schermlezer alleen ruis op; wat telt is de samenvatting.
  const ariaLabel = `${practicedCount} van ${weeklyActivity.length} dagen geoefend deze week${weeklyGoal ? `, weekdoel ${weeklyGoal} dagen` : ''}`;
  return <div className={`flex flex-col gap-2 w-full ${className}`} data-id="element-2781" data-mp="1hekb28r6p5il">
      <div role="img" aria-label={ariaLabel} className="w-full" data-id="element-2782" data-mp="1cdecvver8q1o">
        <div className="flex items-start w-full" data-id="element-2783" data-mp="1cg8yhq9i8gwo">
          {weeklyActivity.map((day, index) => {
          const size = nodeSize(day);
          const prev = weeklyActivity[index - 1];
          // Een segment licht alleen op als BEIDE aangrenzende dagen geoefend
          // zijn — zo beloont het lint aaneengesloten oefenen en blijven
          // gaten zichtbaar.
          const segmentLit = Boolean(prev?.practiced && day.practiced);
          return <React.Fragment key={day.label} data-id="element-2784" data-mp="1uhejbiovx5u5" data-mpi={index}>
                {index > 0 && <div className="flex-1 flex items-center h-[38px] px-0.5" data-id="element-2785" data-mp="1zfg2s4q83765">
                    <motion.div initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                delay: index * 0.04,
                type: 'spring',
                stiffness: 260,
                damping: 24
              }} className={`h-1.5 w-full origin-left rounded-full ${segmentLit ? TRACK_FILL[color] : 'bg-slate-200'}`} data-id="element-2786" data-mp="1zdbvdct47q14" />
                  
                  </div>}

                <div className="flex flex-col items-center gap-1.5 shrink-0" data-id="element-2787" data-mp="12pym6uppc0po">
                  <div className="h-[38px] flex items-center justify-center relative" data-id="element-2788" data-mp="16qhesqzf5asx">
                    {day.isToday && <motion.span aria-hidden="true" animate={{
                  scale: [1, 1.35, 1],
                  opacity: [0.7, 0, 0.7]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className={`absolute rounded-full ${HALO[color]}`} style={{
                  width: size,
                  height: size
                }} data-id="element-2789" data-mp="sqg30ghm8iyo" />}
                    <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: index * 0.04,
                  type: 'spring',
                  stiffness: 300,
                  damping: 20
                }} style={{
                  width: size,
                  height: size
                }} className={`relative rounded-full border-2 flex items-center justify-center ${day.practiced ? `${NODE_FILL[color]} text-white` : 'bg-white border-slate-200'}`} data-id="element-2790" data-mp="o41kmacirxcq">
                      
                      {day.practiced && <Check className="w-3.5 h-3.5" strokeWidth={3} data-id="element-2791" data-mp="1ftwac84ahp1x" />}
                    </motion.div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase ${day.isToday ? 'text-slate-800' : 'text-slate-500'}`} data-id="element-2792" data-mp="1iv96htm033tn">
                    
                    {day.label}
                  </span>
                </div>
              </React.Fragment>;
        })}
        </div>
      </div>

      {!hideSummary && weeklyGoal ? <p className="text-xs font-semibold text-slate-500 text-center" data-id="element-2793" data-mp="1xjvndzra03mz">
          {practicedCount} / {weeklyGoal} dagen geoefend
          {goalReached && <span className="ml-1 text-emerald-600" data-id="element-2794" data-mp="21l7b1zqlaesj">Gehaald!</span>}
        </p> : null}
    </div>;
}